/**
 * Route-level convenience guards. Protection lives in the server middleware (PRD §12.4) — these
 * only keep the browser from rendering a page it will never get data for.
 */

import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { homePathFor, useSession, type Role } from "@/lib/session";

function Pending() {
  return <div className="min-h-screen bg-background" aria-busy="true" />;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useSession();
  const location = useLocation();

  if (isLoading) return <Pending />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (user.passwordSetupRequired && location.pathname !== "/set-password") {
    return <Navigate to="/set-password" replace />;
  }
  return <>{children}</>;
}

export function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user, isLoading } = useSession();

  if (isLoading) return <Pending />;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to={homePathFor(user)} replace />;
  return <>{children}</>;
}
